import React from 'react';
import { TabsContent } from '../../../components/ui';
import { DocumentTextIcon, ClipboardIcon, ClockIcon } from '../../../components/ui/Icons';
import { CustomSplitViewWithTabs } from '../../../components/generic/SplitViewWithTabs';
import DocumentDetailsTab from './components/tabs/DocumentDetailsTab';
import DocumentRevisionsTab from './components/tabs/DocumentRevisionsTab';
import DocumentTimelineTab from './components/tabs/DocumentTimelineTab';
import DocumentAuditLogTab from './components/tabs/DocumentAuditLogTab';
import ProcedureAssignmentsTab from '../../procedure-management/components/ProcedureAssignmentsTab';
import { useFlexibleList } from '../../../hooks/useSWR';

const CustomDocumentSplitView = ({
  isOpen,
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  labels,
  onClose,
  onRecordUpdate,
}) => {
  const { data: documentRevisions = [] } = useFlexibleList(
    selectedRecord?.id ? `/document-management/revision/by-document/${selectedRecord.id}` : null
  );
  const { data: timelineEntries = [] } = useFlexibleList(
    selectedRecord?.id ? `/document-management/document-timeline-entry/by-document/${selectedRecord.id}` : null
  );
  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/master/1/${selectedRecord.id}` : null
  );

  const showAssignments = config?.features?.procedureAssignments === true;
  const { data: procedureAssignments = [] } = useFlexibleList(
    showAssignments && selectedRecord?.id
      ? `/procedure-assignment/by-document/${selectedRecord.id}`
      : null
  );

  const tabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: null,
      badgeColor: 'bg-green-600',
    },
    {
      value: 'revisions',
      label: 'Revisions',
      icon: <ClipboardIcon className="w-4 h-4" />,
      badge: documentRevisions.length,
      badgeColor: 'bg-blue-600',
    },
    ...(showAssignments
      ? [
          {
            value: 'assignments',
            label: 'Viewers',
            icon: <DocumentTextIcon className="w-4 h-4" />,
            badge: procedureAssignments.length,
            badgeColor: 'bg-purple-600',
          },
        ]
      : []),
    {
      value: 'timeline',
      label: 'Timeline',
      icon: <ClockIcon className="w-4 h-4" />,
      badge: timelineEntries.length,
      badgeColor: 'bg-orange-600',
    },
    {
      value: 'audit',
      label: 'Audit Log',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: 'bg-red-600',
    },
  ];

  return (
    <CustomSplitViewWithTabs
      isOpen={isOpen}
      config={config}
      labels={labels}
      headerTitle={
        selectedRecord
          ? `Document - ${selectedRecord.code || selectedRecord.title || selectedRecord.id}`
          : 'Select a Document to view details'
      }
      selectedRecord={selectedRecord}
      emptyMessage={labels.noSelectionText || 'No Document selected'}
      tabs={tabs}
      onClose={onClose}
    >
      <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
        <DocumentDetailsTab
          selectedRecord={selectedRecord}
          selectedRecordFiles={selectedRecordFiles}
          config={config}
          fields={fields}
        />
      </TabsContent>
      <TabsContent value="revisions" className="mt-0 flex-1 flex flex-col min-h-0">
        <DocumentRevisionsTab
          selectedRecord={selectedRecord}
          onRevisionUpdate={onRecordUpdate}
          config={config}
        />
      </TabsContent>
      {showAssignments && (
        <TabsContent value="assignments" className="mt-0 flex-1 flex flex-col min-h-0 overflow-hidden">
          <ProcedureAssignmentsTab
            selectedRecord={selectedRecord}
            onAssignmentsSaved={onRecordUpdate}
          />
        </TabsContent>
      )}
      <TabsContent value="timeline" className="mt-0 flex-1 flex flex-col min-h-0">
        <DocumentTimelineTab selectedRecord={selectedRecord} />
      </TabsContent>
      <TabsContent value="audit" className="mt-0 flex-1 flex flex-col min-h-0">
        <DocumentAuditLogTab selectedRecord={selectedRecord} />
      </TabsContent>
    </CustomSplitViewWithTabs>
  );
};

export default CustomDocumentSplitView;
